import { Space } from '@looker/components';
import { BIButton, BIIcon } from '@pulsepointinc/bi-components';
import type { AllowedButtons, Driver, DriveStep } from 'driver.js';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { isNextDisabled, isPreviousDisabled } from './driver';
import type { TourStep } from './types';

interface PopoverFooterProps {
  driver: Driver;
}

export const StepFooter: React.FC<PopoverFooterProps> = ({ driver }) => {
  const defaultButtonsState = [!isNextDisabled(driver) && 'next', !isPreviousDisabled(driver) && 'previous'].filter(
    Boolean,
  ) as AllowedButtons[];

  const [buttons, setButtons] = useState(defaultButtonsState);

  const { activeElement, activeStep, config, state } = useMemo(() => {
    const activeElement = driver.getActiveElement();
    const activeStep = driver.getActiveStep() as TourStep;
    const config = driver.getConfig();
    const state = driver.getState();

    return { activeElement, activeStep, config, state };
  }, [driver]);

  const handleNextClick = () => {
    if (activeElement && activeStep && config.onNextClick) {
      config.onNextClick(activeElement, activeStep as DriveStep, {
        config,
        state,
        driver,
      });
    } else {
      driver.moveNext();
    }
  };

  const handlePrevClick = () => {
    if (activeElement && activeStep && config.onPrevClick) {
      config.onPrevClick(activeElement, activeStep as DriveStep, {
        config,
        state,
        driver,
      });
    } else {
      driver.movePrevious();
    }
  };

  const handleQuitClick = () => {
    if (driver.onComplete) {
      const activeIndex = driver.isDrivingThroughChildren ? driver.lastRootIndex : driver.getActiveIndex();
      driver.onComplete(activeIndex ?? 0, true);
    }

    driver.destroy();
  };

  useEffect(() => {
    const interval = window.setInterval(() => {
      const buttons = [!isNextDisabled(driver) && 'next', !isPreviousDisabled(driver) && 'previous'].filter(
        Boolean,
      ) as AllowedButtons[];

      setButtons(buttons);
    }, 500);

    return () => window.clearInterval(interval);
  }, [driver, activeStep]);

  return (
    <Space between width="100%">
      <BIButton color="invisible" iconBefore={<BIIcon icon="Close" />} onClick={handleQuitClick}>
        Quit Demo
      </BIButton>
      <Space gap="u2" justify="end">
        <BIButton color="secondary" disabled={!buttons.includes('previous')} onClick={handlePrevClick}>
          Back
        </BIButton>
        <BIButton disabled={!buttons.includes('next')} onClick={handleNextClick}>
          {driver.isLastStep() && !driver.isDrivingThroughChildren ? 'Done' : 'Next'}
        </BIButton>
      </Space>
    </Space>
  );
};
